"use client";

import { AgenticCaseProgressIndicator } from "@/components/case-checking/AgenticCaseProgressIndicator";
import {
  caseCheckProgressIndicatorVariant,
  compactCaseProgressLabel,
  type AgenticCaseProgressCase,
} from "@/components/case-checking/agenticCaseProgress";
import type { CaseAgenticMissionLink } from "@/services/api/case-checking";

import { useCaseProgress } from "./useCaseProgress";

export function CaseProgressBadge({
  caseId,
  caseData,
  missionLink,
  enabled = true,
  size = "sm",
  className,
  initialTaskId,
  pollWhileActive,
  compact = false,
  onProgressClick,
  onRefresh,
  testId,
}: {
  caseId: string;
  caseData?: AgenticCaseProgressCase | null;
  missionLink?: CaseAgenticMissionLink | null;
  enabled?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  initialTaskId?: string | null;
  pollWhileActive?: boolean;
  compact?: boolean;
  onProgressClick?: (taskId: string) => void;
  onRefresh?: () => void;
  testId?: string;
}) {
  const progress = useCaseProgress({
    caseId,
    caseSnapshot: caseData,
    missionLink,
    enabled,
    initialTaskId,
    pollWhileActive,
    onRefresh,
  });

  if (!progress.visible) return null;
  if (!progress.hasResolvedActiveTask && !progress.label) return null;

  const displayLabel = compact
    ? compactCaseProgressLabel(progress.label, progress.missionLink)
    : progress.label;
  if (!displayLabel) return null;

  const indicatorSize = compact || progress.displayMode === "minimized" ? "minimized" : size;
  const canOpenDialog = Boolean(progress.taskId && onProgressClick);
  const runnerInFlight = Boolean(
    progress.taskId && (progress.taskStream.isInProgress || progress.taskStream.isLoadingInitial),
  );

  const fullLabel = progress.label;

  return (
    <AgenticCaseProgressIndicator
      label={displayLabel}
      title={compact && fullLabel && fullLabel !== displayLabel ? fullLabel : undefined}
      variant={caseCheckProgressIndicatorVariant(progress.missionLink, runnerInFlight)}
      size={indicatorSize}
      className={className}
      testId={testId ?? `case-progress-badge-${caseId}`}
      onClick={
        canOpenDialog && progress.taskId
          ? () => onProgressClick?.(progress.taskId!)
          : undefined
      }
    />
  );
}
